import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { getTodayLocalDateString } from '@/utils/dateUtils';

export type HabitColor = {
  empty: string;
  filled: string;
  hover: string;
};

export type Habit = {
  id: string;
  name: string;
  color: HabitColor;
  completedDates: string[]; // Array of ISO date strings
  createdAt: string;
  order?: number; // Optional order field
  active?: boolean; // Whether the habit is active or paused
  isCollapsed?: boolean; // Whether the habit view is collapsed
  pinned?: boolean; // Whether the habit is pinned
};

type HabitContextType = {
  habits: Habit[];
  loading: boolean;
  addHabit: (name: string, color: HabitColor) => Promise<void>;
  deleteHabit: (id: string) => Promise<void>;
  updateHabit: (id: string, updates: Partial<Omit<Habit, 'id' | 'createdAt'>>) => Promise<void>;
  toggleHabitCompletion: (habitId: string, date: string, onFirstCompletionToday?: () => void) => Promise<void>;
  isHabitCompletedOnDate: (habitId: string, date: string) => boolean;
  reorderHabits: (habitOrders: { id: string, order: number }[]) => Promise<void>;
  toggleHabitPin: (habitId: string) => Promise<void>;
  pinnedHabits: Habit[];
  maxFreeHabits: number;
  activeHabitsCount: number;
  canAddMoreHabits: boolean;
};

type HabitOrderUpdate = {
  id: string;
  order: number;
};

const SupabaseHabitContext = createContext<HabitContextType | undefined>(undefined);

// Default color schemes
export const defaultColors: HabitColor = {
  empty: 'hsl(var(--habit-empty))',
  filled: 'hsl(var(--habit-filled))',
  hover: 'hsl(var(--habit-hover))'
};

export const predefinedColors: HabitColor[] = [
  // Purple (default)
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(var(--habit-filled))',
    hover: 'hsl(var(--habit-hover))'
  },
  // Blue
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(210, 90%, 60%)',
    hover: 'hsl(210, 90%, 70%)'
  },
  // Green
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(142, 71%, 45%)',
    hover: 'hsl(142, 71%, 55%)'
  },
  // Orange
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(24, 95%, 65%)',
    hover: 'hsl(24, 95%, 75%)'
  },
  // Pink
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(330, 90%, 65%)',
    hover: 'hsl(330, 90%, 75%)'
  },
  // Teal
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(174, 86%, 45%)',
    hover: 'hsl(174, 86%, 55%)'
  },
  // Red
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(0, 90%, 65%)',
    hover: 'hsl(0, 90%, 75%)'
  },
  // Lime
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(90, 76%, 56%)',
    hover: 'hsl(90, 76%, 66%)'
  },
  // Indigo
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(250, 70%, 65%)',
    hover: 'hsl(250, 70%, 75%)'
  },
  // Amber
  {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(45, 93%, 58%)',
    hover: 'hsl(45, 93%, 68%)'
  },
];

// Helper function to create a HabitColor from a hex value
export const createColorFromHex = (hexColor: string): HabitColor => {
  return {
    empty: 'hsl(var(--habit-empty))',
    filled: hexColor,
    hover: hexColor, // For simplicity, using same color for hover
  };
};

export function SupabaseHabitProvider({ children }: { children: ReactNode }) {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isPremium } = useAuth();

  // Add constant for max free habits
  const MAX_FREE_HABITS = 3;

  // Add helper function to check if user can add more habits
  const canAddMoreHabits = () => {
    if (isPremium) return true;
    return habits.filter(h => h.active !== false).length < MAX_FREE_HABITS;
  };

  // Fetch user's habits from Supabase when user is authenticated
  useEffect(() => {
    const fetchHabits = async () => {
      if (!user) {
        console.log("No user, clearing habits");
        setHabits([]);
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching habits for user:", user.id);
        setLoading(true);
        
        // Fetch habits with order by clause
        const { data: habitsData, error: habitsError } = await supabase
          .from('habits')
          .select('*')
          .eq('user_id', user.id)
          .order('order', { ascending: true })
          .order('created_at', { ascending: true });
        
        if (habitsError) {
          console.error("Error fetching habits:", habitsError);
          throw habitsError;
        }
        
        console.log("Fetched habits data:", habitsData);
        
        // For each habit, fetch its completions
        const habitsWithCompletions = await Promise.all(
          habitsData.map(async (habit) => {
            const { data: completionsData, error: completionsError } = await supabase
              .from('habit_completions')
              .select('date')
              .eq('habit_id', habit.id);
            
            if (completionsError) {
              console.error("Error fetching completions for habit:", habit.id, completionsError);
              throw completionsError;
            }
            
            console.log("Fetched completions for habit:", habit.id, completionsData);
            
            return {
              id: habit.id,
              name: habit.name,
              color: {
                empty: habit.color_empty,
                filled: habit.color_filled,
                hover: habit.color_hover,
              },
              completedDates: completionsData.map(c => c.date),
              createdAt: habit.created_at,
              order: habit.order || null,
              active: habit.active !== false, // Default to active if not specified
              isCollapsed: habit.is_collapsed || false, // Default to expanded if not specified
              pinned: habit.pinned || false, // Default to unpinned if not specified
            };
          })
        );
        
        console.log("Setting habits with completions:", habitsWithCompletions);
        setHabits(habitsWithCompletions);
      } catch (error) {
        console.error("Error fetching habits:", error);
        toast.error("Failed to load habits. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchHabits();
  }, [user]);

  const addHabit = async (name: string, color: HabitColor = defaultColors) => {
    if (!user) {
      toast.error("Please sign in to add a habit");
      return;
    }

    // Check if user can add more habits
    if (!canAddMoreHabits()) {
      toast.error(
        <div className="flex flex-col gap-2">
          <p>You've reached the maximum number of habits for free users (3).</p>
          <p>Upgrade to Premium to add unlimited habits!</p>
        </div>
      );
      return;
    }
    
    try {
      // Shift all existing habits down by 1
      const habitUpdates = habits.map((habit, index) => ({
        id: habit.id,
        order: (habit.order || 0) + 1
      }));
      
      // Update the order of existing habits in the database
      for (const update of habitUpdates) {
        await supabase
          .from('habits')
          .update({ order: update.order })
          .eq('id', update.id);
      }
      
      // Insert the new habit at order 0 (top position)
      const { data, error } = await supabase
        .from('habits')
        .insert({
          name,
          color_empty: color.empty,
          color_filled: color.filled,
          color_hover: color.hover,
          user_id: user.id,
          order: 0 // Always insert at the top
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Add to local state at the beginning of the array
      const newHabit: Habit = {
        id: data.id,
        name: data.name,
        color: {
          empty: data.color_empty,
          filled: data.color_filled,
          hover: data.color_hover,
        },
        completedDates: [],
        createdAt: data.created_at,
        order: data.order
      };
      
      // Put the new habit at the beginning and update orders of existing habits
      const updatedHabits = habits.map(habit => ({
        ...habit,
        order: (habit.order || 0) + 1
      }));
      
      setHabits([newHabit, ...updatedHabits]);
    } catch (error: any) {
      console.error("Error adding habit:", error);
      toast.error(error.message || "Failed to add habit");
    }
  };

  const deleteHabit = async (id: string) => {
    if (!user) return;
    
    try {
      // Delete from Supabase
      const { error } = await supabase
        .from('habits')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Update local state
      setHabits(habits.filter((habit) => habit.id !== id));
    } catch (error: any) {
      console.error("Error deleting habit:", error);
      toast.error(error.message || "Failed to delete habit");
    }
  };

  const updateHabit = async (id: string, updates: Partial<Omit<Habit, 'id' | 'createdAt'>>) => {
    if (!user) return;
    
    try {
      // Extract updates for the database
      const dbUpdates: any = {};
      if (updates.name) dbUpdates.name = updates.name;
      if (updates.color) {
        dbUpdates.color_empty = updates.color.empty;
        dbUpdates.color_filled = updates.color.filled;
        dbUpdates.color_hover = updates.color.hover;
      }
      if (updates.active !== undefined) {
        dbUpdates.active = updates.active;
      }
      if (updates.isCollapsed !== undefined) {
        dbUpdates.is_collapsed = updates.isCollapsed;
      }
      if (updates.pinned !== undefined) {
        dbUpdates.pinned = updates.pinned;
      }
      
      // Special case for completedDates
      if (updates.completedDates !== undefined) {
        // For completedDates, we need to sync with the completions table
        // First, get current completions
        const { data: currentCompletions, error: fetchError } = await supabase
          .from('habit_completions')
          .select('date')
          .eq('habit_id', id);
        
        if (fetchError) throw fetchError;
        
        const currentDates = currentCompletions.map(c => c.date);
        const newDates = updates.completedDates;
        
        // Dates to add (in newDates but not in currentDates)
        const datesToAdd = newDates.filter(date => !currentDates.includes(date));
        
        // Dates to remove (in currentDates but not in newDates)
        const datesToRemove = currentDates.filter(date => !newDates.includes(date));
        
        // Add new dates
        if (datesToAdd.length > 0) {
          const completionsToAdd = datesToAdd.map(date => ({
            habit_id: id,
            date
          }));
          
          const { error: insertError } = await supabase
            .from('habit_completions')
            .insert(completionsToAdd);
          
          if (insertError) throw insertError;
        }
        
        // Remove dates that are no longer completed
        if (datesToRemove.length > 0) {
          const { error: deleteError } = await supabase
            .from('habit_completions')
            .delete()
            .eq('habit_id', id)
            .in('date', datesToRemove);
          
          if (deleteError) throw deleteError;
        }
      }
      
      // Update the habit record if there are fields to update
      if (Object.keys(dbUpdates).length > 0) {
        const { error: updateError } = await supabase
          .from('habits')
          .update(dbUpdates)
          .eq('id', id);
        
        if (updateError) throw updateError;
      }
      
      // Update local state
      setHabits(
        habits.map((habit) => {
          if (habit.id === id) {
            const updatedHabit = { ...habit };
            if (updates.name) updatedHabit.name = updates.name;
            if (updates.color) updatedHabit.color = updates.color;
            if (updates.completedDates) updatedHabit.completedDates = updates.completedDates;
            if (updates.order !== undefined) updatedHabit.order = updates.order;
            if (updates.active !== undefined) updatedHabit.active = updates.active;
            if (updates.isCollapsed !== undefined) updatedHabit.isCollapsed = updates.isCollapsed;
            if (updates.pinned !== undefined) updatedHabit.pinned = updates.pinned;
            return updatedHabit;
          }
          return habit;
        })
      );
    } catch (error: any) {
      console.error("Error updating habit:", error);
      toast.error(error.message || "Failed to update habit");
    }
  };

  const toggleHabitCompletion = async (habitId: string, date: string, onFirstCompletionToday?: () => void) => {
    if (!user) return;
    
    try {
      const habit = habits.find((h) => h.id === habitId);
      if (!habit) return;
      
      const isCompleted = habit.completedDates.includes(date);
      
      if (isCompleted) {
        // Delete completion
        const { error } = await supabase
          .from('habit_completions')
          .delete()
          .eq('habit_id', habitId)
          .eq('date', date);
        
        if (error) throw error;
        
        // Update local state
        setHabits(
          habits.map((h) => {
            if (h.id === habitId) {
              return {
                ...h,
                completedDates: h.completedDates.filter((d) => d !== date)
              };
            }
            return h;
          })
        );
      } else {
        // Add completion

        // Check if this is the first completion for this date across all active habits
        const activeHabits = habits.filter(h => h.active !== false);
        const anyOtherHabitCompletedToday = activeHabits.some(h => 
          h.id !== habitId && h.completedDates.includes(date)
        );

        if (!anyOtherHabitCompletedToday && onFirstCompletionToday) {
          onFirstCompletionToday();
        }

        const { error } = await supabase
          .from('habit_completions')
          .insert({
            habit_id: habitId,
            date
          });
        
        if (error) throw error;
        
        // Update local state
        setHabits(
          habits.map((h) => {
            if (h.id === habitId) {
              return {
                ...h,
                completedDates: [...h.completedDates, date]
              };
            }
            return h;
          })
        );
      }
    } catch (error: any) {
      console.error("Error toggling habit completion:", error);
      toast.error(error.message || "Failed to update habit progress");
    }
  };

  const reorderHabits = async (habitOrders: HabitOrderUpdate[]) => {
    if (!user) return;
    
    try {
      // Update the order in local state first (optimistic update)
      const updatedHabits = [...habits].sort((a, b) => {
        const aOrder = habitOrders.find(h => h.id === a.id)?.order || 0;
        const bOrder = habitOrders.find(h => h.id === b.id)?.order || 0;
        return aOrder - bOrder;
      });
      
      setHabits(updatedHabits);
      
      // Update the order in the database
      for (const { id, order } of habitOrders) {
        const { error } = await supabase
          .from('habits')
          .update({ order })
          .eq('id', id);
        
        if (error) {
          console.error("Error updating habit order:", error);
          throw error;
        }
      }
    } catch (error: any) {
      console.error("Error reordering habits:", error);
      toast.error(error.message || "Failed to update habit order");
      
      // Reload habits to ensure UI is in sync with the database
      const { data: habitsData, error: fetchError } = await supabase
        .from('habits')
        .select('*')
        .eq('user_id', user.id)
        .order('order', { ascending: true });
      
      if (!fetchError && habitsData) {
        // Re-sync the local state with the database
        const reloadedHabits = await Promise.all(
          habitsData.map(async (habit) => {
            const { data: completionsData } = await supabase
              .from('habit_completions')
              .select('date')
              .eq('habit_id', habit.id);
            
            return {
              id: habit.id,
              name: habit.name,
              color: {
                empty: habit.color_empty,
                filled: habit.color_filled,
                hover: habit.color_hover,
              },
              completedDates: (completionsData || []).map(c => c.date),
              createdAt: habit.created_at,
              order: habit.order,
              active: habit.active ?? true,
              isCollapsed: habit.is_collapsed ?? false
            };
          })
        );
        
        setHabits(reloadedHabits);
      }
    }
  };

  const isHabitCompletedOnDate = (habitId: string, date: string): boolean => {
    const habit = habits.find((h) => h.id === habitId);
    return habit ? habit.completedDates.includes(date) : false;
  };

  // Add getter for active habits count
  const getActiveHabitsCount = () => habits.filter(h => h.active !== false).length;

  // Toggle pin status for a habit (max 4 pinned habits)
  const toggleHabitPin = async (habitId: string) => {
    const habit = habits.find(h => h.id === habitId);
    if (!habit) return;

    const currentPinnedCount = habits.filter(h => h.pinned).length;
    
    // If trying to pin and already at max limit, show error
    if (!habit.pinned && currentPinnedCount >= 4) {
      toast.error("Maximum 4 habits can be pinned");
      return;
    }

    await updateHabit(habitId, { pinned: !habit.pinned });
    toast.success(habit.pinned ? "Habit unpinned" : "Habit pinned");
  };

  // Get pinned habits - maintain consistent order based on main habit order
  const pinnedHabits = habits
    .filter(h => h.pinned && h.active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <SupabaseHabitContext.Provider
      value={{
        habits,
        loading,
        addHabit,
        deleteHabit,
        updateHabit,
        toggleHabitCompletion,
        isHabitCompletedOnDate,
        reorderHabits,
        toggleHabitPin,
        pinnedHabits,
        // Add new values to context
        maxFreeHabits: MAX_FREE_HABITS,
        activeHabitsCount: getActiveHabitsCount(),
        canAddMoreHabits: canAddMoreHabits(),
      }}
    >
      {children}
    </SupabaseHabitContext.Provider>
  );
}

export function useHabits() {
  const context = useContext(SupabaseHabitContext);
  if (context === undefined) {
    throw new Error('useHabits must be used within a SupabaseHabitProvider');
  }
  return context;
}
